import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, Sparkles, Crown } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';

const fmt = (amount, currency, lang) => new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: (currency || 'eur').toUpperCase(), minimumFractionDigits: amount % 1 ? 2 : 0 }).format(amount);

export const PricingCard = ({ compact = false }) => {
  const { t, lang } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [interval, setInterval] = useState('yearly');
  const [plans, setPlans] = useState(null);
  const [configured, setConfigured] = useState(true);
  const [busy, setBusy] = useState(false);
  const p = t.pricing;

  useEffect(() => { api.plans().then((r) => { setPlans(r.plans); setConfigured(r.configured !== false); }).catch(() => setPlans({})); }, []);

  const plan = plans?.[interval];
  const monthly = plans?.monthly?.amount;
  const yearly = plans?.yearly?.amount;
  const saving = monthly && yearly ? Math.round((1 - yearly / (monthly * 12)) * 100) : 0;
  const isPro = user && user.plan === 'pro';

  const subscribe = async () => {
    if (!user) { navigate('/login', { state: { next: '/upgrade' } }); return; }
    if (isPro) { navigate('/account'); return; }
    if (!configured) { toast.error(p.notConfigured); return; }
    setBusy(true);
    try {
      const { checkout_url } = await api.checkout(interval);
      window.location.href = checkout_url;
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  return (
    <div className="relative rounded-3xl border-2 border-primary bg-card p-8 shadow-xl shadow-primary/10" data-testid="pricing-card">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-xs font-semibold text-primary-foreground flex items-center gap-1"><Crown className="h-3 w-3" /> {p.plan}</div>

      <div className="flex justify-center pt-2">
        <div className="inline-flex rounded-full bg-secondary p-1 text-sm font-medium" data-testid="billing-toggle">
          <button data-testid="billing-monthly" onClick={() => setInterval('monthly')} className={`px-4 py-1.5 rounded-full transition-colors ${interval === 'monthly' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>{p.monthly}</button>
          <button data-testid="billing-yearly" onClick={() => setInterval('yearly')} className={`px-4 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${interval === 'yearly' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>{p.yearly}{saving > 0 && <span className="text-[10px] font-bold text-primary">-{saving}%</span>}</button>
        </div>
      </div>

      <div className="text-center mt-6">
        {plans === null ? <Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" /> : plan ? (
          <>
            <div className="flex items-end justify-center gap-1">
              <span className="font-display text-5xl font-bold" data-testid="plan-price">{fmt(interval === 'yearly' ? plan.amount / 12 : plan.amount, plan.currency, lang)}</span>
              <span className="text-muted-foreground mb-1.5">{p.period}</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{interval === 'yearly' ? `${fmt(plan.amount, plan.currency, lang)} ${p.billedYearly}` : p.billedMonthly}</p>
          </>
        ) : <p className="text-sm text-destructive">{p.unavailable}</p>}
      </div>

      {!compact && (
        <ul className="mt-8 space-y-3">
          {p.features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm">
              <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-secondary flex items-center justify-center text-primary"><Check className="h-3.5 w-3.5" /></span>{f}
            </li>
          ))}
        </ul>
      )}

      <Button onClick={subscribe} disabled={busy || plans === null || !plan} data-testid="subscribe-btn" className="mt-8 w-full rounded-xl font-semibold h-11 gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" /> {isPro ? p.manage : p.cta}</>}
      </Button>
      <p className="mt-3 text-center text-xs text-muted-foreground">{p.note}</p>
      <p className="mt-1.5 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5" data-testid="paypal-note">
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M7.1 21.3H3.9c-.4 0-.7-.4-.6-.8L6.4 2.9c.1-.5.5-.8 1-.8h6.9c2.3 0 4 .6 4.9 1.7.9 1 1.1 2.4.7 4.3-.8 4-3.4 6.1-7.6 6.1H9.7c-.5 0-.9.3-1 .8l-1 5.6c-.1.4-.3.7-.6.7z"/></svg>
        {p.paypal}
      </p>
    </div>
  );
};
